import React, { useMemo, useState } from "react";
import { Icon } from "../../../components/Component";

/**
 * Códigos que devuelve el backend por cada fila del Excel.
 *
 * La tabla anterior los interpretaba al revés: pintaba el 2 de rojo y el 0 de
 * naranja, así que los errores de verdad —un árbol que no existe, una foto que
 * falta— se veían menos graves que un simple aviso.
 */
const ERROR = 0;
const CORRECTO = 1;
const AVISO = 2;

const Resumen = ({ color, icono, cantidad, texto }) => (
  <div className={`d-flex align-items-center me-4 mb-2 text-${color}`}>
    <Icon name={icono} className="me-1" style={{ fontSize: "1.35rem" }} />
    <span className="fs-4 fw-bold me-1">{cantidad}</span>
    <span className="text-soft">{texto}</span>
  </div>
);

const ListaFilas = ({ filas, color }) => (
  <ul className="list-unstyled mb-0">
    {filas.map(({ indice, message }) => (
      <li key={indice} className="d-flex py-1 border-bottom">
        <span className="text-soft me-3" style={{ minWidth: "3.5rem" }}>
          Fila {indice}
        </span>
        <span className={`text-${color}`}>{message}</span>
      </li>
    ))}
  </ul>
);

/**
 * Resultado de una importación de Excel.
 *
 * Antes se listaban las cientos de filas del archivo, una a una, casi todas con
 * el mismo texto de «actualizado correctamente». Con 115 árboles eso es una
 * pared de texto donde lo único que hace falta mirar —lo que falló— queda
 * enterrado. Aquí se muestra el recuento y se detallan solo los problemas; los
 * correctos quedan plegados por si alguien quiere revisarlos.
 */
const ResultadosImportacion = ({ items }) => {
  const [verCorrectos, setVerCorrectos] = useState(false);

  const { correctos, avisos, errores } = useMemo(() => {
    const grupos = { correctos: [], avisos: [], errores: [] };

    (items || []).forEach((item, i) => {
      const fila = { indice: i + 1, message: item.message };

      if (item.status === CORRECTO) grupos.correctos.push(fila);
      else if (item.status === AVISO) grupos.avisos.push(fila);
      else if (item.status === ERROR) grupos.errores.push(fila);
    });

    return grupos;
  }, [items]);

  if (!items || items.length === 0) return null;

  const todoBien = errores.length === 0 && avisos.length === 0;

  return (
    <div className="mt-4">
      <h6 className="mb-3">Resultado de la importación</h6>

      <div
        className={`d-flex flex-wrap align-items-center p-3 rounded mb-3 ${
          todoBien ? "bg-lighter" : "bg-warning-dim"
        }`}
      >
        {correctos.length > 0 && (
          <Resumen
            color="success"
            icono="check-circle-fill"
            cantidad={correctos.length}
            texto={correctos.length === 1 ? "árbol correcto" : "árboles correctos"}
          />
        )}
        {avisos.length > 0 && (
          <Resumen
            color="warning"
            icono="alert-circle-fill"
            cantidad={avisos.length}
            texto={avisos.length === 1 ? "con aviso" : "con avisos"}
          />
        )}
        {errores.length > 0 && (
          <Resumen
            color="danger"
            icono="cross-circle-fill"
            cantidad={errores.length}
            texto={errores.length === 1 ? "con error" : "con errores"}
          />
        )}
      </div>

      {/* Lo que necesita atención va desplegado; no hay que buscarlo. */}
      {errores.length > 0 && (
        <div className="mb-3">
          <h6 className="text-danger mb-2">
            No se pudieron guardar ({errores.length})
          </h6>
          <ListaFilas filas={errores} color="danger" />
        </div>
      )}

      {avisos.length > 0 && (
        <div className="mb-3">
          <h6 className="text-warning mb-2">
            Guardados con aviso ({avisos.length})
          </h6>
          <ListaFilas filas={avisos} color="warning" />
        </div>
      )}

      {correctos.length > 0 && (
        <div>
          <button
            type="button"
            className="btn btn-sm btn-link ps-0"
            onClick={() => setVerCorrectos(v => !v)}
          >
            {verCorrectos ? "Ocultar" : "Ver"} los {correctos.length} correctos
          </button>

          {verCorrectos && (
            <div
              className="mt-1"
              style={{ maxHeight: "16rem", overflowY: "auto" }}
            >
              <ListaFilas filas={correctos} color="success" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResultadosImportacion;
